// CobranzasPage.tsx

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';

import {
  getCobranzasGenesysErrores,
  getCobranzasGenesysEstado,
  useCancelarCobranzasGenesysMutation,
  useCobranzasCampaignMutation,
  useIniciarCobranzasGenesysMutation,
} from '@/shared/actions/campanas/cobranzas.actions';
import {
  GeneralTable,
  SingleTableBoxScene,
} from '@/shared/components/ui';
import { useTableFilter } from '@/shared/hooks';
import { useColumnsCobranzas } from '../../hooks';
import {
  CampaignTypeSelector,
  cobranzasCampaignLabels,
  GenesysSendModal,
  type GenesysSendStatus,
  SummaryCards,
} from '../../components';
import {
  buildCobranzasRow,
  exportCobranzasPayloadToExcel,
  extractGenesysErrores,
  extractGenesysEstadoInfo,
  extractGenesysIniciarInfo,
  type GenesysSendInfo,
  type CobranzasRow,
} from '../../utils';
import type {
  CobranzasCampaignResponse,
  CobranzasCampaignType,
} from '../../types';

const POLL_INTERVAL_MS = 3000;

const terminalStatus = (
  estado: NonNullable<GenesysSendInfo['estado']>
): GenesysSendStatus =>
  estado === 'COMPLETADO'
    ? 'success'
    : estado === 'INTERRUMPIDO'
      ? 'interrupted'
      : 'error';

const CobranzasPage: React.FC = () => {
  ///* table
  const { pagination, setPagination } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  ///* campaña seleccionada
  const [campaignType, setCampaignType] = useState<CobranzasCampaignType | ''>(
    ''
  );

  ///* excluir registros con valor 0 en la consulta
  const [soloConValor, setSoloConValor] = useState(false);

  const campaignLabel = campaignType
    ? cobranzasCampaignLabels[campaignType]
    : '';

  ///* mes de la campaña (mes actual)
  const mesActual = useMemo(() => {
    const now = new Date();
    const month = new Intl.DateTimeFormat('es-ES', { month: 'long' }).format(now);
    return `${month.charAt(0).toUpperCase()}${month.slice(1)} ${now.getFullYear()}`;
  }, []);

  ///* consulta del payload de la campaña (solo al pulsar "Consultar")
  const {
    data,
    mutateAsync,
    isPending: isLoading,
    isError,
    error,
  } = useCobranzasCampaignMutation();

  const campaignResponse = data as CobranzasCampaignResponse | undefined;

  ///* envío asíncrono a Genesys (iniciar -> poll estado -> detener)
  const {
    mutateAsync: iniciarGenesys,
    isPending: isStarting,
  } = useIniciarCobranzasGenesysMutation();

  const {
    mutateAsync: cancelarGenesys,
    isPending: isCancelling,
  } = useCancelarCobranzasGenesysMutation();

  const [sendOpen, setSendOpen] = useState(false);
  const [hasSent, setHasSent] = useState(false);
  const [sendBarDismissed, setSendBarDismissed] = useState(false);

  const [sendRunId, setSendRunId] = useState<string | null>(null);
  const [sendStatus, setSendStatus] = useState<GenesysSendStatus>('idle');
  const [sendInfo, setSendInfo] = useState<GenesysSendInfo>({ errores: [] });
  const [sendErrorMsg, setSendErrorMsg] = useState('');
  const [sendNotice, setSendNotice] = useState('');
  const [sendErroresDetalle, setSendErroresDetalle] = useState<unknown[]>([]);
  const [isLoadingErrores, setIsLoadingErrores] = useState(false);
  const [sendElapsed, setSendElapsed] = useState(0);
  const [cancelRequested, setCancelRequested] = useState(false);

  const isRunning = sendStatus === 'pending' || sendStatus === 'running';

  ///* cronometro del envío
  useEffect(() => {
    if (!isRunning) return;

    const timer = setInterval(
      () => setSendElapsed((seconds) => seconds + 1),
      1000
    );

    return () => clearInterval(timer);
  }, [isRunning]);

  ///* poll al estado del envío mientras CORRIENDO
  useEffect(() => {
    if (!sendRunId || sendStatus !== 'running') return;

    let cancelled = false;
    let inFlight = false;

    const poll = async () => {
      if (inFlight || cancelled) return;
      inFlight = true;

      try {
        const response = await getCobranzasGenesysEstado(sendRunId);
        if (cancelled) return;

        const info = extractGenesysEstadoInfo(response);
        setSendInfo(info);

        if (!info.estado || info.estado === 'CORRIENDO') return;

        setSendRunId(null);
        setCancelRequested(false);
        setSendStatus(terminalStatus(info.estado));

        if (info.estado === 'ERROR') {
          setSendErrorMsg(
            info.errores.length > 0
              ? 'El proceso terminó con errores.'
              : 'El proceso terminó en error.'
          );
        }
      } catch (error) {
        if (cancelled) return;

        setSendRunId(null);
        setCancelRequested(false);
        setSendStatus('error');

        const status = (error as any)?.response?.status;
        setSendErrorMsg(
          status === 404
            ? 'La ejecución ya no existe en el microservicio (posible reinicio). El proceso se perdió; puede reintentar el envío.'
            : (error as any)?.response?.data?.message ||
                (error as any)?.response?.data?.detail ||
                (error as any)?.message ||
                'No se pudo consultar el progreso del envío.'
        );
      } finally {
        inFlight = false;
      }
    };

    const timer = setInterval(poll, POLL_INTERVAL_MS);
    poll();

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [sendRunId, sendStatus]);

  ///* porcentaje de avance de envío: enviados / pendientes
  const sendProgress = useMemo(() => {
    if (sendStatus === 'idle' || sendStatus === 'pending') return 0;
    if (sendStatus !== 'running') return 100;

    const { enviados, pendientes } = sendInfo;
    if (typeof pendientes !== 'number') return 0;
    if (pendientes <= 0) return 100;

    return Math.min(100, Math.round(((enviados ?? 0) / pendientes) * 100));
  }, [sendStatus, sendInfo]);

  ///* tiempo restante estimado a partir del ritmo de bloques
  const sendEtaSeconds = useMemo(() => {
    if (sendStatus !== 'running' || !sendInfo.fechaInicio) return undefined;

    const { bloquesOk = 0, bloquesTotal = 0 } = sendInfo;
    if (bloquesOk <= 0 || bloquesTotal <= bloquesOk) return undefined;

    const transcurridos =
      (Date.now() - new Date(sendInfo.fechaInicio).getTime()) / 1000;
    if (!Number.isFinite(transcurridos) || transcurridos <= 0) return undefined;

    const porBloque = transcurridos / bloquesOk;
    const restante = porBloque * (bloquesTotal - bloquesOk);

    return Number.isFinite(restante) && restante > 0 ? restante : undefined;
  }, [sendStatus, sendInfo]);

  const sendErrorMessage =
    sendErrorMsg ||
    'No se pudo completar el envío de la lista de contactos.';

  ///* filas construidas a partir de la respuesta (payload Genesys)
  const rows = useMemo<CobranzasRow[]>(() => {
    if (!campaignResponse?.data) return [];

    return campaignResponse.data.map((item, index) =>
      buildCobranzasRow(item, index)
    );
  }, [campaignResponse]);

  ///* paginacion en cliente (la respuesta no pagina)
  const pagedRows = useMemo(
    () => rows.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize),
    [rows, pageIndex, pageSize]
  );

  ///* cronometro para consultas largas (30s o mas)
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    if (!isLoading) {
      setElapsedSeconds(0);
      return;
    }

    const timer = setInterval(
      () => setElapsedSeconds((seconds) => seconds + 1),
      1000
    );

    return () => clearInterval(timer);
  }, [isLoading]);

  ///* handlers
  const handleChangeCampaignType = (type: CobranzasCampaignType) => {
    setCampaignType(type);
    setPagination({ pageIndex: 0, pageSize: 10 });
  };

  const handleSearch = async () => {
    if (!campaignType) return;

    setPagination({ pageIndex: 0, pageSize: 10 });

    try {
      await mutateAsync({ type: campaignType, soloConValor });
    } catch {
      // el error se muestra via Alert (mutation.error)
    }
  };

  const handleSend = async () => {
    if (!campaignType || isRunning) return;

    setHasSent(true);
    setSendBarDismissed(false);
    setSendOpen(true);
    setSendRunId(null);
    setSendStatus('pending');
    setSendInfo({ errores: [] });
    setSendErrorMsg('');
    setSendNotice('');
    setSendErroresDetalle([]);
    setIsLoadingErrores(false);
    setSendElapsed(0);
    setCancelRequested(false);

    try {
      const response = await iniciarGenesys(campaignType);
      const { id, message } = extractGenesysIniciarInfo(response);

      if (!id) {
        setSendStatus('error');
        setSendErrorMsg(
          message || 'El microservicio no devolvió un id de ejecución.'
        );
        return;
      }

      setSendRunId(id);
      setSendStatus('running');
    } catch (error) {
      const err = error as any;
      const status = err?.response?.status;
      const data = err?.response?.data || {};

      // 409: ya hay un envío en curso de la misma campaña; nos enganchamos
      // al proceso activo usando el id que devuelve el back.
      if (status === 409 && typeof data.id === 'string') {
        setSendRunId(data.id);
        setSendStatus('running');
        setSendNotice(
          data.message || 'Ya hay un envío en curso; se reanudó su seguimiento.'
        );
        return;
      }

      setSendStatus('error');
      setSendErrorMsg(
        data.message ||
          data.detail ||
          err?.message ||
          'No se pudo iniciar el envío.'
      );
    }
  };

  const handleCancel = async () => {
    if (!sendRunId || isCancelling || cancelRequested) return;

    try {
      await cancelarGenesys(sendRunId);
    } catch {
      // si falla la peticion, se sigue esperando: el micro puede cortar igual
    }
    setCancelRequested(true);
  };

  const handleViewErrores = async () => {
    if (!campaignType || isLoadingErrores) return;

    setIsLoadingErrores(true);

    try {
      const response = await getCobranzasGenesysErrores(campaignType);
      setSendErroresDetalle(extractGenesysErrores(response));
    } catch (error) {
      setSendErroresDetalle([
        (error as any)?.response?.data?.message ||
          'No se pudieron obtener los errores de la campaña.',
      ]);
    } finally {
      setIsLoadingErrores(false);
    }
  };

  const handleCloseSend = () => {
    setSendOpen(false);
  };

  ///* columns
  const { columnsCobranzas } = useColumnsCobranzas();

  const errorMessage = isError
    ? (error as any)?.response?.data?.message ||
      (error as any)?.response?.data?.detail ||
      (error as any)?.message ||
      'No se pudo obtener la información de la campaña.'
    : '';

  const canDownload =
    !!campaignType &&
    !!campaignResponse?.data &&
    campaignResponse.data.length > 0;

  const handleDownload = () => {
    if (!canDownload || !campaignResponse?.data) return;
    exportCobranzasPayloadToExcel(campaignResponse.data, campaignType!);
  };

  ///* contenido del banner de resultado
  const sendBanner = (() => {
    if (sendStatus === 'error') {
      return {
        severity: 'error' as const,
        text: `Error en la campaña ${campaignLabel}: ${sendErrorMessage}`,
      };
    }

    if (sendStatus === 'interrupted') {
      return {
        severity: 'warning' as const,
        text: [
          `Campaña ${campaignLabel}: envío detenido (INTERRUMPIDO)`,
          ...(typeof sendInfo.enviados === 'number'
            ? [`Enviados: ${sendInfo.enviados.toLocaleString()}`]
            : []),
          ...(typeof sendInfo.faltantes === 'number' &&
          sendInfo.faltantes > 0
            ? [`Faltan: ${sendInfo.faltantes.toLocaleString()}`]
            : []),
          ...(typeof sendInfo.bloquesError === 'number' &&
          sendInfo.bloquesError > 0
            ? [`Bloques error: ${sendInfo.bloquesError}`]
            : []),
          ...(sendInfo.errores.length > 0
            ? [`Errores: ${sendInfo.errores.length}`]
            : []),
        ].join(' · '),
      };
    }

    return {
      severity: 'success' as const,
      text: [
        `Campaña ${campaignLabel}: procesada correctamente`,
        ...(typeof sendInfo.enviados === 'number'
          ? [`Enviados: ${sendInfo.enviados.toLocaleString()}`]
          : []),
        ...(typeof sendInfo.bloquesError === 'number' &&
        sendInfo.bloquesError > 0
          ? [`Bloques error: ${sendInfo.bloquesError}`]
          : []),
        ...(sendInfo.errores.length > 0
          ? [`Errores: ${sendInfo.errores.length}`]
          : []),
      ].join(' · '),
    };
  })();

  return (
    <SingleTableBoxScene
      title="Campañas de Cobranzas"
      customContentButtonSide={
        <Stack direction="row" spacing={1} alignItems="center">
          <CalendarMonthOutlinedIcon color="primary" fontSize="small" />
          <Typography variant="body2" color="text.secondary">
            Mes actual de campaña: <strong>{mesActual}</strong>
          </Typography>
        </Stack>
      }
    >
      <Stack spacing={1} sx={{ mb: 2 }}>
        <CampaignTypeSelector
          value={campaignType}
          onChange={handleChangeCampaignType}
          onSearch={handleSearch}
          onSend={handleSend}
          onDownload={handleDownload}
          canDownload={canDownload}
          loading={isLoading}
          sending={isStarting || isRunning}
          soloConValor={soloConValor}
          onSoloConValorChange={setSoloConValor}
        />
      </Stack>

      {isRunning && !sendOpen && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <CircularProgress size={18} color="secondary" />
            <Typography variant="body2">
              Enviando lista de contactos de la campaña {campaignLabel} a
              Genesys... {Math.round(sendProgress)}%
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Typography variant="caption" color="text.secondary">
              {sendElapsed} s
            </Typography>
            <Button
              size="small"
              startIcon={<VisibilityOutlinedIcon />}
              onClick={() => setSendOpen(true)}
            >
              Ver avance
            </Button>
            {sendStatus === 'running' && !cancelRequested && (
              <Button
                size="small"
                color="error"
                startIcon={<StopCircleIcon />}
                onClick={handleCancel}
                disabled={isCancelling}
              >
                Cancelar
              </Button>
            )}
          </Stack>
          <LinearProgress
            variant="determinate"
            value={sendProgress}
            color="secondary"
            sx={{ height: 8, borderRadius: 4 }}
          />
          {sendNotice && (
            <Typography variant="caption" color="info.main">
              {sendNotice}
            </Typography>
          )}
          {cancelRequested && (
            <Typography variant="caption" color="warning.main">
              Cancelación solicitada, esperando confirmación del servidor...
            </Typography>
          )}
        </Box>
      )}

      {hasSent && !isRunning && !sendOpen && !sendBarDismissed && (
        <Alert severity={sendBanner.severity} sx={{ mb: 2 }} action={<IconButton
            size="small"
            aria-label="Cerrar"
            onClick={() => setSendBarDismissed(true)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>}>
          {sendBanner.text}
        </Alert>
      )}

      {isLoading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Consultando información de la campaña {campaignLabel}. Esta consulta
          puede tardar un minuto o más. Tiempo transcurrido: {elapsedSeconds} s.
        </Alert>
      )}

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error en la campaña {campaignLabel}: {errorMessage}
        </Alert>
      )}

      {campaignResponse && !isError && (
        <Box sx={{ mb: 2 }}>
          <SummaryCards data={campaignResponse} />
        </Box>
      )}

      {campaignResponse && !isError && (
        <GeneralTable<CobranzasRow>
          columns={columnsCobranzas}
          data={pagedRows}
          isLoading={isLoading}
          enableGlobalFilter={false}
          enableColumnFilters={false}
          enableSorting={false}
          enableColumnActions={false}
          enableEditing={false}
          showCustomActionBtnSpace={false}
          pagination={pagination}
          onPaging={setPagination}
          rowCount={rows.length}
          getRowId={(row) => row._id}
        />
      )}

      <GenesysSendModal
        open={sendOpen}
        campaignLabel={
          campaignType ? cobranzasCampaignLabels[campaignType] : ''
        }
        status={sendStatus}
        progress={sendProgress}
        elapsedSeconds={sendElapsed}
        info={sendInfo}
        cancelRequested={cancelRequested}
        isCancelling={isCancelling}
        etaSeconds={sendEtaSeconds}
        notice={sendNotice}
        errorDetalle={sendErroresDetalle}
        isLoadingErrores={isLoadingErrores}
        errorMessage={sendErrorMessage}
        onClose={handleCloseSend}
        onCancel={handleCancel}
        onViewErrores={handleViewErrores}
      />
    </SingleTableBoxScene>
  );
};

export default CobranzasPage;
